import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ReorderItemMatch = {
  originalItem: any;
  menuItem: any | null;
  status:
    | 'available'
    | 'unavailable'
    | 'price_changed'
    | 'modifiers_changed'
    | 'required_missing';
  selected: boolean;
  price: number;
  priceDelta: number;
  matchedModifiers: any[];
  unavailableModifiers: any[];
};

export type ReorderStatus =
  | 'idle'
  | 'fetching'
  | 'ready'
  | 'applying'
  | 'scheduling'
  | 'closed'
  | 'error';

export type ReorderState = {
  status: ReorderStatus;
  order: any | null;
  matchedItems: ReorderItemMatch[];
  error: string | null;
  modalOpen: boolean;
  scheduledTime: string | null;
  restaurantDetails: any | null;
};

export const INITIAL_STATE: ReorderState = {
  status: 'idle',
  order: null,
  matchedItems: [],
  error: null,
  modalOpen: false,
  scheduledTime: null,
  restaurantDetails: null,
};

const reorderSlice = createSlice({
  name: 'reorder',
  initialState: INITIAL_STATE,
  reducers: {
    startReorder(state, action: PayloadAction<any>) {
      return {
        ...state,
        status: 'fetching',
        order: action.payload,
        matchedItems: [],
        error: null,
        modalOpen: true,
        scheduledTime: null,
        restaurantDetails: null,
      };
    },
    setReorderResult(state, action: PayloadAction<ReorderItemMatch[]>) {
      return {
        ...state,
        status: 'ready',
        matchedItems: action.payload,
      };
    },
    toggleItemSelection(state, action: PayloadAction<number>) {
      const idx = action.payload;
      if (
        state.matchedItems[idx] &&
        state.matchedItems[idx].status !== 'unavailable' &&
        state.matchedItems[idx].status !== 'required_missing'
      ) {
        const items = [...state.matchedItems];
        items[idx] = { ...items[idx], selected: !items[idx].selected };
        return { ...state, matchedItems: items };
      }
      return state;
    },
    setReorderError(state, action: PayloadAction<string>) {
      return {
        ...state,
        status: 'error',
        error: action.payload,
      };
    },
    setReorderClosed(state, action: PayloadAction<string>) {
      return {
        ...state,
        status: 'closed',
        error: action.payload,
      };
    },
    setReorderScheduling(state, action: PayloadAction<any | undefined>) {
      return {
        ...state,
        status: 'scheduling',
        error: null,
        restaurantDetails:
          action.payload !== undefined
            ? action.payload
            : state.restaurantDetails,
      };
    },
    setReorderScheduledTime(state, action: PayloadAction<string>) {
      return {
        ...state,
        scheduledTime: action.payload,
      };
    },
    retryReorderWithTime(
      state,
      // eslint-disable-next-line no-unused-vars
      _action: PayloadAction<{ start: number; end: number }>
    ) {
      return {
        ...state,
        status: 'fetching',
        matchedItems: [],
        error: null,
      };
    },
    // eslint-disable-next-line no-unused-vars
    applyReorder(state, _action: PayloadAction<{ navigate: Function }>) {
      return {
        ...state,
        status: 'applying' as const,
      };
    },
    closeReorderModal() {
      return {
        ...INITIAL_STATE,
      };
    },
    resetReorder() {
      return { ...INITIAL_STATE };
    },
  },
});

export const {
  startReorder,
  setReorderResult,
  toggleItemSelection,
  setReorderError,
  setReorderClosed,
  setReorderScheduling,
  setReorderScheduledTime,
  retryReorderWithTime,
  applyReorder,
  closeReorderModal,
  resetReorder,
} = reorderSlice.actions;

export default reorderSlice.reducer;
